import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { formValueSelector, change, submit } from 'redux-form';
import { connect } from 'react-redux';
import { map, get, isEmpty, filter, assign, omit } from 'lodash';
import { CREATE_ITEM_FORM } from '../../constants/formNames';
import { inventoryErrors, priceOptions } from './utils';
import CreateItemForm from './CreateItemForm';
import { injectIntl, intlShape } from 'react-intl';

class CreateItemFormContainer extends Component {
  constructor (props) {
    super(props);

    this.state = {
      addOnImgUrl: '',
      addOnImgId: '',
      submitPhoto: false
    };

    this.handleCategoryChange = this.handleCategoryChange.bind(this);
    this.handlePhotoChange = this.handlePhotoChange.bind(this);
    this.handleRemovePhotoClick = this.handleRemovePhotoClick.bind(this);
    this.changeSubmitPhotoState = this.changeSubmitPhotoState.bind(this);
  }

  componentDidMount () {
    const {
      isEditAddOnModal,
      fromManageAddons,
      initialValues = {},
      spaceId
    } = this.props;
    const { currency = {}, ...restOfValues } = initialValues;
    const propsData = assign({}, {
      space_id: spaceId,
      origin: fromManageAddons
        ? this.props.intl.formatMessage({ id: 'inbox.create_item_form_container.inventory', defaultMessage: 'Inventory' })
        : this.props.intl.formatMessage({ id: 'inbox.create_item_form_container.custom', defaultMessage: 'Custom Offer' })
    }, isEditAddOnModal ?
      {
        currency_symbol: currency.symbol,
        currency_type: currency.code,
        ...restOfValues
      } : {});

    this.setState({
      addOnImgId: get(this.props, 'initialValues.image_ids[0]', '')
    });

    analyticsIntegrations.createItemFormViewed(`${isEditAddOnModal ? 'edit' : 'create'} item form viewed`, omit(propsData, ['group_id', 'taxes', 'fees']));
  }

  handleCategoryChange () {
    this.props.dispatch(change(CREATE_ITEM_FORM, 'subcategory', null));
  }

  handlePhotoChange (photoUrl, photoId) {
    const { isEditAddOnModal, dispatch } = this.props;

    this.setState({
      addOnImgUrl: photoUrl,
      addOnImgId: photoId,
      submitPhoto: true
    });

    if (isEditAddOnModal) {
      dispatch(submit(CREATE_ITEM_FORM));
    }
  }

  handleRemovePhotoClick () {
    this.setState({
      addOnImgUrl: '',
      addOnImgId: '',
      submitPhoto: true
    }, () => {
      this.props.dispatch(submit(CREATE_ITEM_FORM));
    });
  }

  changeSubmitPhotoState () {
    this.setState({
      submitPhoto: false
    });
  }

  render () {
    const { addOnImgUrl, addOnImgId, submitPhoto } = this.state;
    const categoryOptions = map(this.props.categories, category => {
      return {
        value: category.id,
        label: category.name
      };
    });

    return (
      <CreateItemForm
        {...this.props}
        handleCategoryChange={this.handleCategoryChange}
        handlePhotoChange={this.handlePhotoChange}
        handleRemovePhotoClick={this.handleRemovePhotoClick}
        changeSubmitPhotoState={this.changeSubmitPhotoState}
        categoryOptions={categoryOptions}
        priceOptions={priceOptions}
        addOnImgUrl={addOnImgUrl}
        addOnImgId={addOnImgId}
        submitPhoto={submitPhoto}
      />
    );
  }
}

CreateItemFormContainer.propTypes = {
  currency: PropTypes.string,
  categories: PropTypes.array,
  subCategoryOptions: PropTypes.array,
  dispatch: PropTypes.func,
  isEditAddOnModal: PropTypes.bool,
  fromManageAddons: PropTypes.bool,
  initialValues: PropTypes.object,
  spaceId: PropTypes.string,
  intl: intlShape
};

const selector = formValueSelector(CREATE_ITEM_FORM);

function mapStateToProps (state, ownProps) {
  const addOnFeeValues = selector(state, 'fees');
  const addOnTaxValues = selector(state, 'taxes');
  const category = selector(state, 'category');
  const subcategories = get(state, `env.addOns.subcategories[${category}]`);
  const categories = get(state, 'env.addOns.categories');
  const nonVatTaxTypes = get(state, 'env.addOns.nonVatTaxTypes');
  const isSubmitting = get(state, 'inventory.isPosting');
  const isFetching = get(state, 'inventory.isFetching');
  const inventoryItem = get(state, 'inventory.item');
  const { auth, add_ons, profile } = state;
  const addItemsForm = get(state, 'form.ADD_ITEMS_FORM.values.addOns');
  const inventoryErrorCode = get(state, 'inventory.error', null);
  const inventoryError = get(inventoryErrors, inventoryErrorCode, null);
  const isVatEnabled = get(state, 'env.web.is_vat_enabled', null);

  const subcategoryOptions = map(subcategories, subcategory => {
    return {
      value: subcategory.id,
      label: subcategory.name
    };
  });

  const returnObj = {
    addOnFeeValues,
    addOnTaxValues,
    categories,
    subcategoryOptions,
    isSubmitting,
    isFetching,
    auth,
    add_ons,
    profile,
    addItemsForm,
    nonVatTaxTypes,
    inventoryError,
    isVatEnabled
  };

  if (ownProps.isEditAddOnModal && !isEmpty(inventoryItem)) {
    const { visible_on_listing, unit_cost, count, ...itemDetails } = inventoryItem;
    // clean this taxes filter up this once VAT goes live
    const taxes = filter(inventoryItem.additional_charges, { charge_type: 'tax' });
    // This is how we should filter addons with VAT UI
    const vatTaxes = map(inventoryItem.additional_charges, (charge => {
      if ('fee' !== get(charge, 'charge_type', '')) {
        const other_label = 'tax' === charge.charge_type ? 'other' : null;
        if (other_label) {
          return {
            ...charge,
            other_label: charge.label,
            label: 'other'
          };
        }

        return { ...charge };
      }
    }));

    const fees = filter(inventoryItem.additional_charges, { charge_type: 'fee' });

    const taxesToMap = isVatEnabled ? vatTaxes : taxes;
    const taxesArray = map(taxesToMap, tax => {
      if ('%' === tax.charge_unit) {
        return {
          ...tax,
          amount: tax.amount * 100
        };
      }

      return tax;
    });

    const feesArray = map(fees, fee => {
      if ('%' === fee.charge_unit) {
        return {
          ...fee,
          amount: fee.amount * 100
        };
      }

      return {
        ...fee,
        amount: fee.amount.toFixed(2)
      };
    });

    return {
      ...returnObj,
      initialValues: {
        ...itemDetails,
        unit_cost: unit_cost.toFixed(2),
        visible_on_listing: visible_on_listing ? 'true' : 'false',
        taxes: taxesArray,
        fees: feesArray,
        count: count || null
      }
    };
  }

  return returnObj;
}

export default injectIntl(connect(mapStateToProps)(CreateItemFormContainer));
